import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tag, Plus, Trash2, Search, Save, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

const DoctorPricing = ({ embedded = false }) => {
  const { token } = useAuth();
  const [allPricing, setAllPricing] = useState([]);
  const [allDoctorsData, setAllDoctorsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");

  // Build a flat de-duplicated list of all doctors fetched from API
  const allDoctors = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const doc of allDoctorsData) {
      if (!seen.has(doc.name)) {
        seen.add(doc.name);
        list.push(doc);
      }
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, "ar"));
  }, [allDoctorsData]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pricingRes, doctorsRes] = await Promise.all([
        axios.get(`${API}/doctor-pricing`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/doctors`),
      ]);
      setAllPricing(pricingRes.data);
      setAllDoctorsData(doctorsRes.data.doctors || []);
    } catch {
      toast.error("فشل تحميل قوائم الأسعار");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const loadDoctorPricing = async (name) => {
    setSelectedDoctor(name);
    if (!name) {
      setServices([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/doctor-pricing/${encodeURIComponent(name)}`);
      setServices(res.data.services || []);
    } catch {
      setServices([]);
    }
  };

  const addService = () => setServices(prev => [...prev, { name: "", price: "" }]);
  const updateService = (idx, field, val) => {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  };
  const removeService = (idx) => setServices(prev => prev.filter((_, i) => i !== idx));

  const copyFromDoctor = async (sourceName) => {
    if (!sourceName) return;
    try {
      const res = await axios.get(`${API}/doctor-pricing/${encodeURIComponent(sourceName)}`);
      const copied = res.data.services || [];
      if (copied.length === 0) { toast.error("لا توجد أسعار لهذا الطبيب"); return; }
      if (services.filter(s => s.name).length > 0 && !window.confirm(`سيتم استبدال القائمة الحالية بأسعار "${sourceName}" (${copied.length} خدمة). متابعة؟`)) return;
      setServices(copied.map(s => ({ ...s })));
      toast.success(`تم نسخ ${copied.length} خدمة من ${sourceName} — اضغط "حفظ" للتأكيد`);
    } catch {
      toast.error("فشل نسخ الأسعار");
    }
  };

  const handleSave = async () => {
    if (!selectedDoctor) {
      toast.error("اختر طبيباً أولاً");
      return;
    }
    const cleaned = services.filter(s => s.name && s.price);
    setSaving(true);
    try {
      await axios.put(`${API}/doctor-pricing`,
        { doctor_name: selectedDoctor, services: cleaned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("تم حفظ قائمة الأسعار");
      setServices(cleaned);
      fetchAll();
    } catch {
      toast.error("فشل في الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDoctor) return;
    if (!window.confirm(`حذف كل أسعار "${selectedDoctor}"؟`)) return;
    try {
      await axios.delete(`${API}/doctor-pricing/${encodeURIComponent(selectedDoctor)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      setSelectedDoctor("");
      setServices([]);
      fetchAll();
    } catch {
      toast.error("فشل في الحذف");
    }
  };

  const filteredDoctors = allDoctors.filter(d =>
    !search || d.name.includes(search) || (d.role && d.role.includes(search))
  );

  const pricedDoctorNames = new Set(allPricing.map(p => p.doctor_name));

  return (
    <div className="space-y-6" data-testid="doctor-pricing-page">
      {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">أسعار الأطباء</h1>
          <p className="text-slate-500 mt-1">
            سجّل وحدّث قائمة الأسعار لكل طبيب — ستظهر للعملاء في صفحة "أطباء المركز" عند الضغط على الطبيب
          </p>
        </div>
      )}
      {embedded && (
        <p className="text-sm text-slate-500">
          سجّل وحدّث قائمة الأسعار لكل طبيب — ستظهر للعملاء عند الضغط على الطبيب
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Doctor list */}
        <Card className="border-0 shadow-sm lg:col-span-4">
          <CardContent className="pt-6">
            <div className="relative mb-3">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="ابحث عن طبيب..."
                className="pr-10"
                data-testid="pricing-doctor-search"
              />
            </div>
            <p className="text-xs text-slate-400 mb-2">{filteredDoctors.length} طبيب</p>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
              {filteredDoctors.map(doc => {
                const has = pricedDoctorNames.has(doc.name);
                const active = selectedDoctor === doc.name;
                return (
                  <button
                    key={doc.name}
                    onClick={() => loadDoctorPricing(doc.name)}
                    className={`
                      w-full text-right p-3 rounded-lg transition-all flex items-center justify-between gap-2
                      ${active
                        ? "bg-[#655dc6] text-white shadow-sm"
                        : "bg-white hover:bg-[#FAF9FE] border border-slate-100"
                      }
                    `}
                    data-testid={`pricing-doc-${doc.name}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm truncate ${active ? "text-white" : "text-slate-800"}`}>{doc.name}</p>
                      <p className={`text-xs truncate ${active ? "text-white/80" : "text-slate-500"}`}>{doc.role}</p>
                    </div>
                    {has && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-white/25 text-white" : "bg-emerald-100 text-emerald-700"}`}>
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Right: Service editor */}
        <Card className="border-0 shadow-sm lg:col-span-8">
          <CardContent className="pt-6">
            {!selectedDoctor ? (
              <div className="text-center py-16">
                <Tag className="w-16 h-16 mx-auto text-slate-300 mb-3" />
                <h3 className="font-bold text-[#655dc6] mb-1">اختر طبيباً</h3>
                <p className="text-sm text-slate-500">اختر طبيباً من القائمة لإدارة قائمة أسعاره</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 pb-4 border-b">
                  <div>
                    <h3 className="font-bold text-lg text-[#655dc6]">{selectedDoctor}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{services.filter(s => s.name).length} خدمة</p>
                  </div>
                  <div className="flex gap-2">
                    {pricedDoctorNames.has(selectedDoctor) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={handleDelete}
                        data-testid="delete-doctor-pricing"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2"
                      data-testid="save-doctor-pricing"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      حفظ
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {allPricing.filter(p => p.doctor_name !== selectedDoctor && (p.services || []).length > 0).length > 0 && (
                    <div className="flex items-center gap-2 mb-3 p-2.5 bg-sky-50 rounded-lg border border-sky-100">
                      <Copy className="w-4 h-4 text-sky-600 shrink-0" />
                      <span className="text-xs text-sky-800 whitespace-nowrap">نسخ أسعار من:</span>
                      <Select value="" onValueChange={copyFromDoctor}>
                        <SelectTrigger className="h-8 text-xs bg-white flex-1" data-testid="copy-pricing-select">
                          <SelectValue placeholder="اختر طبيباً لنسخ قائمة أسعاره..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allPricing
                            .filter(p => p.doctor_name !== selectedDoctor && (p.services || []).length > 0)
                            .map(p => (
                              <SelectItem key={p.doctor_name} value={p.doctor_name}>
                                {p.doctor_name} ({(p.services || []).length} خدمة)
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {services.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-6">لا توجد خدمات. اضغط "إضافة خدمة" أدناه</p>
                  )}
                  {services.map((s, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 items-center p-2 bg-[#FAF9FE] rounded-lg"
                    >
                      <Input
                        value={s.name}
                        onChange={e => updateService(i, "name", e.target.value)}
                        placeholder="اسم الخدمة"
                        className="col-span-7 bg-white"
                        data-testid={`service-name-${i}`}
                      />
                      <div className="col-span-4 relative">
                        <Input
                          value={s.price}
                          onChange={e => updateService(i, "price", e.target.value)}
                          placeholder="السعر"
                          className="bg-white pl-12"
                          data-testid={`service-price-${i}`}
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">ريال</span>
                      </div>
                      <button
                        onClick={() => removeService(i)}
                        className="col-span-1 text-red-400 hover:text-red-600 flex items-center justify-center"
                        data-testid={`remove-service-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={addService}
                  className="w-full mt-4 border-dashed border-[#655dc6]/40 text-[#655dc6] hover:bg-[#FAF9FE] gap-2"
                  data-testid="add-service-row"
                >
                  <Plus className="w-4 h-4" />
                  إضافة خدمة
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorPricing;
